import { createClient } from '@supabase/supabase-js'
import type { Database } from '../types/supabase'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY
const isMockMode = import.meta.env.VITE_MOCK_AUTH === 'true' || 
                   supabaseUrl?.includes('mock') || 
                   supabaseAnonKey?.includes('mock')

// Log auxiliar em desenvolvimento para confirmar modo
if (import.meta.env.DEV) {
  // eslint-disable-next-line no-console
  console.log('[supabase] Modo mock ativo?', isMockMode, '\nURL:', supabaseUrl)
}

// Validar configuração apenas se não estivermos em modo mock
if (!isMockMode) {
  if (!supabaseUrl) {
    throw new Error('Missing env.VITE_SUPABASE_URL')
  }

  if (!supabaseAnonKey) {
    throw new Error('Missing env.VITE_SUPABASE_ANON_KEY')
  }
}

// Criar cliente com configuração real ou mock
export const supabase = createClient<Database>(
  supabaseUrl || 'https://mock.supabase.co', 
  supabaseAnonKey || 'mock_key',
  {
    auth: {
      autoRefreshToken: !isMockMode,
      persistSession: !isMockMode,
      detectSessionInUrl: !isMockMode,
      flowType: 'pkce', // Recomendado para SPAs
    },
  }
)

// Export do modo mock para uso em outros arquivos
export const isSupabaseMockMode = isMockMode

// Tipos auxiliares para facilitar uso
export type Profile = Database['public']['Tables']['profiles']['Row']
export type ProfileInsert = Database['public']['Tables']['profiles']['Insert']
export type ProfileUpdate = Database['public']['Tables']['profiles']['Update']
export type AuditLog = Database['public']['Tables']['audit_logs']['Row']

// Helper functions para facilitar o uso
export const auth = supabase.auth
export const db = supabase.from('profiles')

// Funções utilitárias para perfis
export const profileUtils = {
  /**
   * Busca o perfil do usuário logado
   */
  getCurrentProfile: async (): Promise<Profile | null> => {
    const { data: { user } } = await auth.getUser()
    if (!user) return null

    const { data, error } = await db
      .select('*')
      .eq('id', user.id)
      .single()

    if (error) throw error
    return data
  },

  /**
   * Atualiza o perfil do usuário logado
   */
  updateCurrentProfile: async (updates: ProfileUpdate): Promise<Profile> => {
    const { data: { user } } = await auth.getUser()
    if (!user) throw new Error('Usuário não autenticado')

    const { data, error } = await db
      .update(updates)
      .eq('id', user.id)
      .select()
      .single()

    if (error) throw error
    return data
  },

  /**
   * Lista todos os perfis (apenas para usuários PRODUCAO)
   */
  getAllProfiles: async (): Promise<Profile[]> => {
    const { data, error } = await db.select('*').order('nome')
    if (error) throw error
    return data || []
  },

  /**
   * Cria um novo perfil (apenas para usuários PRODUCAO)
   */
  createProfile: async (profile: ProfileInsert): Promise<Profile> => {
    const { data, error } = await db.insert(profile).select().single()
    if (error) throw error
    return data
  },

  /**
   * Verifica se o usuário tem permissão para uma ação específica
   */
  checkPermission: async (action: string, resource: string): Promise<boolean> => {
    try {
      const profile = await profileUtils.getCurrentProfile()
      if (!profile) return false

      // Matriz de permissões baseada no PRD atual
      const permissions: Record<Profile['papel'], Record<string, boolean>> = {
        PRODUCAO: {
          'read:users': true,
          'write:users': true,
          'read:events': true,
          'write:events': true,
          'read:agenda': true,
          'write:agenda': true,
        },
        AGIS: {
          'read:users': false,
          'write:users': false,
          'read:events': true,
          'write:events': false,
          'read:agenda': true,
          'write:agenda': false,
        },
        FABRICANTE: {
          'read:users': false,
          'write:users': false,
          'read:events': true,
          'write:events': false,
          'read:agenda': true,
          'write:agenda': true,
        },
        REVENDA: {
          'read:users': false,
          'write:users': false,
          'read:events': true,
          'write:events': false,
          'read:agenda': true,
          'write:agenda': true,
        },
      }

      const userPermissions = permissions[profile.papel] || {}
      return userPermissions[`${action}:${resource}`] || false
    } catch {
      return false
    }
  },
}
